import { motion } from "framer-motion";
import { ArrowUpRight, BarChart3, CalendarClock, Home, Mail, MousePointerClick, Send, Sparkles, Tag } from "lucide-react";
import { SpotlightCard } from "@/components/FeaturedBrands";
import { PageIntro, Reveal } from "@/components/Reveal";
import { CONTACT_EMAIL } from "@/pages/ContactPage";
import { usePageTitle } from "@/lib/hooks";

const SAMPLE = {
  id: "preview", slug: "your-brand", name: "YOUR BRAND", logo_url: null, doc_count: 8,
  categories: ["Cables and Wires", "Switchgear"],
  featured_tagline: "Your message here — up to 140 characters about your latest price list, launch or offer.",
};

const PERKS = [
  { icon: Home, title: "Homepage placement", text: "Your brand sits in the Featured Brands spotlight, right below the hero search, where every visitor scrolls." },
  { icon: Tag, title: "Clearly labelled", text: "Every placement carries a “Sponsored” tag, so visitors trust what they see and your brand looks credible." },
  { icon: CalendarClock, title: "Fixed start and end", text: "Choose how long you run. The spotlight switches off automatically on your end date — no surprises." },
  { icon: BarChart3, title: "Proof it works", text: "Get an emailed report with spotlight views, clicks and click rate, so you can see exactly what you paid for." },
];

const STEPS = [
  { icon: Mail, title: "Email us", text: "Tell us your brand, the dates you want and a short tagline." },
  { icon: Sparkles, title: "We set it up", text: "We add your logo, tagline and end date, and your spotlight goes live." },
  { icon: MousePointerClick, title: "Track results", text: "Receive your views and clicks report by email whenever you need it." },
];

const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Featured spotlight request")}&body=${encodeURIComponent("Brand name:\nPreferred start date:\nPreferred end date:\nTagline (max 140 characters):\nContact person and phone:\n")}`;

const CTA = ({ testId, className = "" }) => (
  <motion.a whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} href={mailto} data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full bg-ph-lime px-7 py-4 font-semibold text-ph-navy shadow-[0_18px_40px_-16px_rgba(182,255,92,0.6)] hover:shadow-[0_0_30px_rgba(182,255,92,0.5)] ${className}`}>
    <Send className="h-4 w-4" /> Request a Featured spotlight <ArrowUpRight className="h-4 w-4" />
  </motion.a>
);

export default function PromotePage() {
  usePageTitle("Promote with PRICEHUB");
  return (
    <>
      <PageIntro eyebrow="Promote with PRICEHUB" title={"GET YOUR BRAND\nIN THE SPOTLIGHT"} desc="Put your brand in front of the contractors, dealers and buyers who come to PRICEHUB to find electrical price lists." />
      <section className="mx-auto max-w-7xl px-5 md:px-8" data-testid="promote-page">
        <CTA testId="promote-cta-top" />
        <Reveal className="mt-16">
          <div className="relative overflow-hidden rounded-[2.25rem] border border-ph-line bg-ph-surface p-5 md:p-10">
            <div className="ph-grid-bg absolute inset-0 opacity-60" />
            <div className="relative grid items-center gap-10 lg:grid-cols-[1fr_1.2fr]">
              <div>
                <p className="font-mono-ph text-xs uppercase tracking-[0.2em] text-ph-accent">Example preview</p>
                <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-ph-ink md:text-4xl">This is what visitors see on the homepage.</h2>
                <p className="mt-4 text-ph-muted">A dark spotlight card with a glowing animated border, your logo, brand name, tagline and a direct link to your price lists page.</p>
              </div>
              <div data-testid="promote-preview-card"><SpotlightCard brand={SAMPLE} preview /></div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PERKS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.07}>
              <div className="h-full rounded-[1.75rem] border border-ph-line bg-ph-surface p-7" data-testid={`promote-perk-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ph-raised text-ph-accent"><p.icon className="h-5 w-5" /></span>
                <p className="mt-6 font-display text-xl font-bold tracking-tight text-ph-ink">{p.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-ph-muted">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#101625] py-24 text-white ring-1 ring-white/5">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <p className="font-mono-ph text-xs uppercase tracking-[0.2em] text-[#B6FF5C]">How to request one</p>
          <h2 className="mt-3 font-display text-4xl font-extrabold tracking-[-0.03em] sm:text-5xl">Three steps. No forms.</h2>
          <ol className="mt-12 grid gap-8 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <li className="relative border-t border-white/15 pt-6" data-testid={`promote-step-${i}`}>
                  <span className="font-display text-6xl font-extrabold text-transparent [-webkit-text-stroke:1.5px_rgba(96,165,250,0.5)]">0{i + 1}</span>
                  <p className="mt-3 flex items-center gap-2 font-display text-2xl font-bold"><s.icon className="h-5 w-5 text-[#B6FF5C]" /> {s.title}</p>
                  <p className="mt-2 text-sm text-slate-300">{s.text}</p>
                </li>
              </Reveal>
            ))}
          </ol>
          <div className="mt-14 flex flex-col gap-4 sm:flex-row sm:items-center">
            <CTA testId="promote-cta-bottom" />
            <p className="text-sm text-slate-300">or write to <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-white underline decoration-[#B6FF5C] underline-offset-4" data-testid="promote-email-link">{CONTACT_EMAIL}</a></p>
          </div>
        </div>
      </section>
    </>
  );
}
